/**
 * First-party CDP visitor identity for the shop.
 *
 * The shop keeps its OWN persistent, pseudonymous visitor id (`cdp_anon_id`) in localStorage so a
 * visitor's touches are tied to one identity across sessions — even without the marketing-site
 * collector JS. When the site forwards its collector anon into the shop (`?cdp_anon_id=…`) and it
 * differs from ours, we keep our persistent id and flag the forwarded one for a server-side merge
 * (the backend union-find collapses the two so both journeys resolve to one profile).
 *
 * The embedded widget is different: it runs in the organiser's own page, next to the collector, so
 * nothing is forwarded in the URL. There the collector's id (its `cdp_anon_id` cookie) is the id the
 * page views are recorded under, so it wins outright — otherwise every widget cart carries an id the
 * "Web traffic" funnel has never seen, and no cart or order is tied to a visit.
 *
 * No PII — this is a random id, only ever mapped to a person server-side on an explicit identify /
 * order (email).
 */

const STORAGE_KEY = "cdp_anon_id";
const FORWARDED_PARAM = "cdp_anon_id";
const COLLECTOR_COOKIE = "cdp_anon_id";

export type CdpVisitor = {
  /** The shop's persistent first-party visitor id (kept across sessions). */
  visitorId: string;
  /** A forwarded collector anon (from ?cdp_anon_id) that differs from visitorId and must be merged. */
  linkedAnonId: string | null;
};

function readStored(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.trim() !== "" ? v : null;
  } catch {
    return null; // private mode / storage blocked
  }
}

function writeStored(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* best-effort */
  }
}

function mint(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `cdp_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

/**
 * The collector's anon id when the web-tracking snippet runs in this same document (the embedded
 * widget on an organiser's site). Asks the collector first, then reads its cookie directly in case
 * the snippet loads after the widget.
 */
function readCollector(): string | null {
  try {
    const fromApi = (window as { cdp?: { getAnonId?: () => string } }).cdp?.getAnonId?.()?.trim();
    if (fromApi) return fromApi;
  } catch {
    /* fall through to the cookie */
  }
  try {
    const match = new RegExp(`(?:^|;\\s*)${COLLECTOR_COOKIE}=([^;]+)`).exec(document.cookie);
    return match?.[1] ? decodeURIComponent(match[1]).trim() || null : null;
  } catch {
    return null;
  }
}

/**
 * Resolve the visitor id for this request, adopting/persisting/merging as needed.
 * `search` and `collector` are injectable for testing; they default to the live page.
 */
export function resolveCdpVisitor(
  search: string = window.location.search,
  collector: string | null = readCollector(),
): CdpVisitor {
  const stored = readStored();

  // Same document as the collector: its id is the visit's id. Adopt it, and hand over an id we
  // minted earlier (before this fix, or before the snippet was installed) so the backend merges it.
  if (collector) {
    if (stored !== collector) writeStored(collector);
    return { visitorId: collector, linkedAnonId: stored && stored !== collector ? stored : null };
  }

  const forwarded = new URLSearchParams(search).get(FORWARDED_PARAM)?.trim() || null;

  // First touch on this shop: adopt a forwarded id if the site sent one, else mint a fresh one.
  if (!stored) {
    const id = forwarded ?? mint();
    writeStored(id);
    return { visitorId: id, linkedAnonId: null };
  }

  // Returning visitor. A different forwarded id means the site tracked this same person under
  // another anon — keep ours and flag the other for a server-side merge.
  if (forwarded && forwarded !== stored) {
    return { visitorId: stored, linkedAnonId: forwarded };
  }

  return { visitorId: stored, linkedAnonId: null };
}

/** ticket-api's heartbeat refuses a longer `visitorId`, and with it the whole beat. */
const MAX_HEARTBEAT_VISITOR_ID = 64;

/**
 * The id the presence heartbeat sends (FL-72), so Shop visitors can count browsers next to tabs.
 * The same id the attribution blob carries. Null when nothing in this browser keeps it — no
 * collector cookie and no storage — because an id minted per tab would count every tab as a person.
 */
export function heartbeatVisitorId(
  search: string = window.location.search,
  collector: string | null = readCollector(),
): string | null {
  const { visitorId } = resolveCdpVisitor(search, collector);
  if (visitorId.length > MAX_HEARTBEAT_VISITOR_ID) return null;
  return visitorId === collector || readStored() === visitorId ? visitorId : null;
}
